import React from 'react';
import { useIntl } from 'react-intl-next';

import { useSearchConfigContext } from '../../../common/search-config-provider';
import { useSearchSessionId } from '../../../common/search-session-provider';
import { useJiraSearchClientContext } from '../../../jira/clients';
import { type JiraFeatures, JiraFeaturesProvider } from '../../../jira/features';
import { messages } from '../../../messages';
import { LegacyContextConverter } from '../../legacy-context-converter';
import { SearchDialogProduct } from '../../product-router';
import {
	type ResultSuppliers,
	type CacheWarmingProps,
	EMPTY_SEARCH_ITEMS,
} from '../../product-router/product';
import { type SearchDialogProductProps } from '../../product-router/product/search-dialog-product';
import { JiraAdvancedSearchProvider } from './jira-advanced-search-provider';
import { JiraAsyncWrapper } from './jira-async-wrapper';
import { type JiraSearchDialogProps } from './jira-search-dialog-types';
import { JiraUserUpdater } from './jira-user-updater';
import { defaultPermissionsSupplier } from './permission-supplier';

export type JiraTabProps = Omit<JiraSearchDialogProps, 'debounceTime'> & {
	features?: JiraFeatures;
} & Pick<SearchDialogProductProps, 'permissionSupplier' | 'order'> &
	Partial<ResultSuppliers> &
	CacheWarmingProps;

export const JiraTab: React.FC<JiraTabProps> = ({ features, formatDate, ...rest }) => {
	const intl = useIntl();
	const { jiraSites } = useSearchConfigContext();
	const { sites, searchClient } = useJiraSearchClientContext();
	const searchSessionId = useSearchSessionId();

	const defaultRecentItemSupplier = () => {
		const context = { sessionId: searchSessionId, referrerId: null };
		return Promise.all([
			searchClient.getRecentIssues(context, sites).promise(),
			searchClient.getRecentBoardsProjectsFilters(context, sites).promise(),
		]).then((results) => ({
			size: results.reduce((sum, { items }) => {
				return sum + items.length;
			}, 0),
			sections: [],
		}));
	};

	// Post query supplier not implemented in Jira until it starts using the generic dialog components
	const defaultPostQueryItemSupplier = () => Promise.resolve(EMPTY_SEARCH_ITEMS);

	const suppliers: ResultSuppliers = {
		preQueryItemSupplier: rest.preQueryItemSupplier || defaultRecentItemSupplier,
		postQueryItemSupplier: rest.postQueryItemSupplier || defaultPostQueryItemSupplier,
	};

	return (
		<LegacyContextConverter>
			<JiraUserUpdater>
				<JiraFeaturesProvider features={features}>
					<JiraAdvancedSearchProvider>
						{(generateAdvancedSearchUrl) => (
							<SearchDialogProduct
								{...rest}
								permissionSupplier={
									jiraSites.length > 0 ? defaultPermissionsSupplier : rest.permissionSupplier
								}
								PLS_DONT_USE_skipDefaultStateManagement
								id="jira"
								title={intl.formatMessage(messages.jira_tab_label)}
								// A note: These sections are not actually interpreted by the Jira tab, but are put here for future use
								sections={[
									{
										id: 'jira.issue',
										title: intl.formatMessage(messages.jira_issues_section_heading),
									},
									{
										id: 'jira.board,project,filter',
										title: intl.formatMessage(
											messages.jira_boards_projects_filters_section_heading,
										),
									},
								]}
								{...suppliers}
								generateAdvancedSearchUrl={generateAdvancedSearchUrl}
								expandedStateInputPlaceholder={intl.formatMessage(messages.jira_search_placeholder)}
							>
								<JiraAsyncWrapper {...rest} debounceTime={250} formatDate={formatDate} />
							</SearchDialogProduct>
						)}
					</JiraAdvancedSearchProvider>
				</JiraFeaturesProvider>
			</JiraUserUpdater>
		</LegacyContextConverter>
	);
};
